import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { isFocusedInboxEnabled } from "owa-mail-triage-common";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";
import folderNameToId from "owa-session-store/lib/utils/folderNameToId";
import { ARCHIVE_DUMPSTER_DISTINGUISHED_ID, PRIMARY_DUMPSTER_DISTINGUISHED_ID } from "owa-folders-constants";
import { type TableQueryType, getCopilotResultsOriginTableViewId, getStore, isCopilotResultsTable, isUnreadSearchFolder, type MailFolderTableQuery, type TableView } from "owa-mail-list-store";

/**
 * Determines whether the Unread | All pivot should be shown for the given table.
 *
 * The pivot replaces the folder name and favourite toggle in the list header, which only matches
 * the surrounding visual language once the user has opted into Win32 styling, so it is gated on
 * `isWin32StylingEnabled` and the `mon-mail-list-unread-pivot` flight. It is also hidden in every
 * folder when Focused Inbox is enabled for the table's account, so that Focused/Other is the only
 * pivot those users see.
 *
 * The pivot is an alternate presentation over the table query's `viewFilter`, so it may only be
 * shown on tables where swapping `viewFilter` between 'All' and 'Unread' is both meaningful and
 * safe. `scenarioType` is allow-listed rather than deny-listed so that new scenario types are
 * excluded by default instead of silently inheriting the pivot.
 *
 * When the Copilot Results tab is selected, the pivot stays visible beside it, so the decision is
 * made against the folder table Copilot Results was opened from.
 */
export default function shouldShowUnreadPivot(selectedTableView: TableView | undefined): boolean {
    if (!isGlobalFeatureEnabled("mon-mail-list-unread-pivot") ||
        !isWin32StylingEnabled() ||
        !selectedTableView) {
        return false;
    }
    
    const tableView = isCopilotResultsTable(selectedTableView.tableQuery)
        ? getStore().tableViews.get(getCopilotResultsOriginTableViewId(getMailboxInfo(selectedTableView)) ?? "")
        : selectedTableView;
    if (!tableView) {
        return false;
    }
    
    const mailboxInfo = getMailboxInfo(tableView);
    if (isFocusedInboxEnabled(mailboxInfo)) {
        return false;
    }
    
    const tableQuery = tableView.tableQuery;
    
    // Excludes Search, Group, GroupFolder, CopilotResults and MyOutlook tables.
    if (tableQuery.type !== 0) {
        return false;
    }
    
    const mailFolderTableQuery = tableQuery as MailFolderTableQuery;
    
    // Excludes group, category, persona, private distribution list and search folder tables, as
    // well as the catch-up, headlines, priority and needs reply views, all of which own the list
    // header themselves and/or carry a viewFilter the pivot must not overwrite.
    if (mailFolderTableQuery.scenarioType !== "mail" &&
        mailFolderTableQuery.scenarioType !== "mailFolderSortBySize") {
        return false;
    }
    
    // A category table carries viewFilter 'UserCategory'. selectFilter throws on 'UserCategory' and
    // rebuilding the query without the category name would silently drop the category.
    if (mailFolderTableQuery.categoryName) {
        return false;
    }
    
    // An Unread search folder is already restricted to unread mail, so the pivot would be both
    // redundant and contradictory.
    if (isUnreadSearchFolder(tableView)) {
        return false;
    }
    
    const folderId = tableQuery.folderId;
    
    // Notes and Recoverable Items do not support view filters, so the filter menu is hidden there.
    if (folderIdToName(folderId) === "notes" ||
        folderId === folderNameToId(PRIMARY_DUMPSTER_DISTINGUISHED_ID, mailboxInfo) ||
        folderId === folderNameToId(ARCHIVE_DUMPSTER_DISTINGUISHED_ID, mailboxInfo)) {
        return false;
    }
    
    return true;
}
